import {
  Building,
  Globe,
  Mail,
  MapPin,
  Phone,
  Send,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";

export default function CityRequest() {
  const [form, setForm] = useState({
    city: "",
    email: "",
    name: "",
    phone: "",
    population: "",
    province: "",
    reason: "",
  });
  const [submitted, setSubmitted] = useState(false);

  // Theme adaptive state
  const [isDark, setIsDark] = useState(
    document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributeFilter: ["class"],
      attributes: true,
    });
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setForm({
      city: "",
      email: "",
      name: "",
      phone: "",
      population: "",
      province: "",
      reason: "",
    });
  };

  return (
    <>
      <AppNavbar />
      <div
        className={`min-h-screen pt-16 ${isDark ? "bg-base-300" : "bg-base-200"}`}
      >
        <Breadcrumbs />

        <section
          className={`relative overflow-hidden ${isDark ? "bg-base-300 text-base-content" : "bg-base-100 text-base-content"}`}
        >
          <div className="absolute inset-0 z-10 opacity-10">
            <img
              alt="City request background"
              className="h-full w-full object-cover"
              src="https://images.unsplash.com/photo-1577948000841-7f7f73c28353?w=1200&h=600&fit=crop&crop=center"
              style={{
                filter: isDark ? "brightness(0.7)" : "none",
              }}
            />
          </div>
          <div className="relative z-20 mx-auto max-w-3xl px-4 py-20">
            <div className="mb-12 text-center">
              <h1 className="mb-4 text-5xl font-bold">Request Your City</h1>
              <p className="text-xl opacity-90">
                Want Hareeng in your city? Let us know where you want us to
                launch next!
              </p>
            </div>
            <form
              className={`flex flex-col gap-6 rounded-2xl p-8 shadow-2xl ${isDark ? "bg-base-200" : "bg-base-100"}`}
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col gap-2">
                <label
                  className={`flex items-center gap-2 font-semibold ${isDark ? "text-base-content" : "text-base-content"}`}
                  htmlFor="city"
                >
                  <MapPin size={18} /> City Name
                </label>
                <input
                  className={`input input-bordered ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                  id="city"
                  name="city"
                  onChange={handleChange}
                  placeholder="e.g. Hlotse"
                  required
                  type="text"
                  value={form.city}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className={`flex items-center gap-2 font-semibold ${isDark ? "text-base-content" : "text-base-content"}`}
                  htmlFor="province"
                >
                  <Globe size={18} /> Province/District
                </label>
                <input
                  className={`input input-bordered ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                  id="province"
                  name="province"
                  onChange={handleChange}
                  placeholder="e.g. Leribe"
                  required
                  type="text"
                  value={form.province}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className={`flex items-center gap-2 font-semibold ${isDark ? "text-base-content" : "text-base-content"}`}
                  htmlFor="population"
                >
                  <Users size={18} /> Estimated Population
                </label>
                <input
                  className={`input input-bordered ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                  id="population"
                  name="population"
                  onChange={handleChange}
                  placeholder="e.g. 500,000"
                  type="text"
                  value={form.population}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className={`flex items-center gap-2 font-semibold ${isDark ? "text-base-content" : "text-base-content"}`}
                  htmlFor="name"
                >
                  <Building size={18} /> Your Name / Organization
                </label>
                <input
                  className={`input input-bordered ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                  id="name"
                  name="name"
                  onChange={handleChange}
                  placeholder="Your name or company"
                  required
                  type="text"
                  value={form.name}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className={`flex items-center gap-2 font-semibold ${isDark ? "text-base-content" : "text-base-content"}`}
                  htmlFor="email"
                >
                  <Mail size={18} /> Email
                </label>
                <input
                  className={`input input-bordered ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                  id="email"
                  name="email"
                  onChange={handleChange}
                  placeholder="you@email.com"
                  required
                  type="email"
                  value={form.email}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className={`flex items-center gap-2 font-semibold ${isDark ? "text-base-content" : "text-base-content"}`}
                  htmlFor="phone"
                >
                  <Phone size={18} /> Phone (optional)
                </label>
                <input
                  className={`input input-bordered ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                  id="phone"
                  name="phone"
                  onChange={handleChange}
                  placeholder="+266 5048 0814"
                  type="text"
                  value={form.phone}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  className={`flex items-center gap-2 font-semibold ${isDark ? "text-base-content" : "text-base-content"}`}
                  htmlFor="reason"
                >
                  <Send size={18} /> Why should Hareeng launch here?
                </label>
                <textarea
                  className={`textarea textarea-bordered min-h-[100px] ${isDark ? "border-base-300 bg-base-300 text-base-content" : ""}`}
                  id="reason"
                  name="reason"
                  onChange={handleChange}
                  placeholder="Tell us about your city, demand, opportunities, etc."
                  required
                  value={form.reason}
                />
              </div>
              <button
                className="btn btn-primary btn-lg flex items-center gap-2"
                disabled={submitted}
                type="submit"
              >
                <Send size={20} />
                {submitted ? "Sending..." : "Submit Request"}
              </button>
              {submitted && (
                <div className="mt-2 text-center font-semibold text-success">
                  Thank you! Your city request has been sent.
                </div>
              )}
            </form>
          </div>
        </section>
      </div>
    </>
  );
}
